import type { QueryClient } from '@tanstack/react-query';

import { useSyncExternalStore } from 'react';

import { requestSession } from 'src/utils/storefrontSession';
import { sessionEpoch, subscribeUserSession } from 'src/utils/userSession';

import { QueryKeys } from './QueryKeys';

/**
 * The session a read made now speaks for (`requestSession()`), for the key of that read
 * (`QueryKeys.forSession`). Re-read when the session changes — in this tab (the session event) or
 * in another one (`storage`). The server snapshot is the same read: the storefront only ever
 * renders statically in tests, and that is where two sessions of one tab are compared.
 */
export const useRequestSession = (): string =>
  useSyncExternalStore(subscribeUserSession, requestSession, requestSession);

/**
 * Drops every read cached for a session other than the one in force now — a tab that lost a
 * session keeps none of its deals in memory (app#248), nor the account and its wallet of the
 * epoch before (api#454). Keyed by the session that asked, they could no longer be served to
 * anyone; this is hygiene, not the guard.
 *
 * The session in force keeps its own: a screen that watches the session may call this on mount
 * without emptying the list it is about to show.
 */
export const removeReadsOfOtherSessions = (queryClient: QueryClient): void => {
  const current = requestSession();
  const epoch = sessionEpoch();

  queryClient.removeQueries({
    predicate: (query) => {
      const session = QueryKeys.sessionOf(query.queryKey);

      if (session !== undefined) return session !== current;

      const [kind, keyEpoch] = query.queryKey;

      return (kind === 'userMe' || kind === 'accountWallet') && keyEpoch !== epoch;
    },
  });
};
